import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ChevronLeft,
  ChevronRight,
  UserPlus,
  ReceiptIndianRupee,
  GraduationCap,
  Users,
  IndianRupee,
  ChevronDown,
  LayoutDashboard,
  BookText,
  ScrollText,
  CalendarDays,
  Building2,
  ListChecks,
  PiggyBank,
  UserCog,
  Laptop,
  ClipboardList,
} from 'lucide-react'
import { getCookie } from '../app/axios/CookieConfig'

// --- Helper Functions ---
const isMobile = () => window.innerWidth < 1024

// --- Sidebar Item Component (Redesigned) ---

const MenuItemComponent = ({
  item,
  isCollapsed,
  openSubmenu,
  setOpenSubmenu,
  handleNavigation,
  isParentMenuActive,
  isActive,
}) => {
  const hasSubmenu = item.submenu && item.submenu.length > 0
  const parentIsActive = isParentMenuActive(item)
  const isSubmenuOpen = openSubmenu === item.id

  const handleMenuClick = () => {
    if (item.submenu) {
      // Toggle submenu
      setOpenSubmenu(openSubmenu === item.id ? null : item.id)
    } else {
      // Navigate to path
      handleNavigation(item.path)
      setOpenSubmenu(null)
    }
  }

  // Base class for all menu items
  const baseClasses =
    'w-full flex items-center justify-between p-3 rounded-2xl transition-all duration-300 ease-in-out cursor-pointer'
  // Classes when collapsed (center aligned)
  const collapsedClasses = 'justify-center w-12 h-12 my-1 mx-auto'
  // Active state classes for Parent Item (Main Menu) - Using a dark, subtle gradient
  const activeClasses = `${item.bgColor} ${item.color} shadow-lg ring-1 ring-offset-2 ring-offset-white/80 ring-current transform hover:scale-[1.01]`
  // Inactive state classes for Parent Item (Main Menu)
  const inactiveClasses = 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'

  return (
    <div className='mb-2'>
      <button
        onClick={handleMenuClick}
        className={`${baseClasses} ${
          parentIsActive ? activeClasses : inactiveClasses
        } ${isCollapsed ? collapsedClasses : 'h-full'}`}
        style={{
          '--tw-ring-color': parentIsActive ? item.color : 'transparent',
          '--tw-border-color': parentIsActive ? item.color : 'transparent',
        }}
      >
        <div
          className={`flex items-center space-x-3 ${
            isCollapsed ? 'justify-center w-full' : 'min-w-0'
          }`}
        >
          {/* Icon Container with more prominent styling */}
          <div
            className={`p-2.5 rounded-xl transition-all duration-300 
              ${
                parentIsActive
                  ? 'bg-white shadow-md'
                  : 'bg-gray-100 group-hover:bg-gray-200'
              }
              ${isCollapsed ? 'p-2' : ''}
            `}
          >
            <item.icon
              className={`w-5 h-5 transition-all duration-300 
                ${
                  parentIsActive
                    ? item.color
                    : 'text-gray-500 group-hover:text-gray-700'
                }
              `}
            />
          </div>

          {/* Text Content */}
          {!isCollapsed && (
            <div className='flex-1 text-left min-w-0'>
              <p
                className={`font-semibold text-sm truncate ${
                  parentIsActive ? 'text-gray-900' : 'text-gray-700'
                }`}
              >
                {item.title}
              </p>
              <p
                className={`text-xs truncate ${
                  parentIsActive ? item.color : 'text-gray-500'
                }`}
              >
                {parentIsActive ? 'Active Section' : item.description}
              </p>
            </div>
          )}
        </div>

        {/* Chevron Icon for Submenu Toggle */}
        {!isCollapsed && hasSubmenu && (
          <div>
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${
                isSubmenuOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
        )}
      </button>

      {/* Submenu Area (New Design) */}
      {hasSubmenu && !isCollapsed && (
        <div
          className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isSubmenuOpen ? 'max-h-96 opacity-100 mt-2' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='ml-6 mt-1 space-y-1 relative pl-4'>
            {/* Vertical line indicator for sub-menu */}
            <div className='absolute left-0 top-0 h-full w-0.5 bg-gray-200 rounded-full' />

            {item.submenu.map((subItem, index) => {
              const subItemIsActive = isActive(subItem.path)
              return (
                <button
                  key={index}
                  onClick={() => handleNavigation(subItem.path)}
                  className={`relative z-10 w-full flex items-center space-x-3 p-2.5 rounded-xl text-sm transition-all duration-200 group hover:bg-indigo-50/70
                        ${
                          subItemIsActive
                            ? 'bg-indigo-100 text-indigo-800 font-semibold'
                            : 'text-gray-700 hover:text-indigo-800'
                        }
                        `}
                >
                  {/* Dot Indicator */}
                  <span
                    className={`absolute -left-[5px] top-1/2 -translate-y-1/2 w-2 h-2 rounded-full transition-all duration-200
                            ${
                              subItemIsActive
                                ? 'bg-indigo-600 ring-4 ring-indigo-200'
                                : 'bg-gray-300 group-hover:bg-indigo-400'
                            }
                            `}
                  ></span>

                  {/* Submenu Icon */}
                  <div
                    className={`p-1.5 rounded-lg transition-all duration-200
                            ${
                              subItemIsActive
                                ? 'bg-indigo-200 text-indigo-700'
                                : 'bg-gray-100 group-hover:bg-indigo-100 text-gray-500 group-hover:text-indigo-600'
                            }
                            `}
                  >
                    <subItem.icon className='w-3.5 h-3.5' />
                  </div>

                  {/* Submenu Text */}
                  <div className='flex-1 text-left min-w-0'>
                    <p className='font-medium truncate'>{subItem.title}</p>
                    <p
                      className={`text-xs truncate ${
                        subItemIsActive ? 'text-indigo-500' : 'text-gray-500'
                      }`}
                    >
                      {subItemIsActive ? 'Current Page' : subItem.description}
                    </p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// --- Main Sidebar Component ---

const Sidebar = ({ isCollapsed, setIsCollapsed }) => {
  const userRole = getCookie('role')
  const navigate = useNavigate()
  const location = useLocation()
  const [openSubmenu, setOpenSubmenu] = useState(null)

  // NOTE: Your original menuItems object is used here. I have slightly modified
  // the 'Finance' submenu icons for better visual differentiation in the new design.
  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/',
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      description: 'Overview & Analytics',
    },
    {
      id: 'students',
      title: 'Students',
      icon: Users,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      description: 'Student Management',
      submenu: [
        {
          title: 'Registration',
          path: '/studentRegistration',
          icon: UserPlus,
          description: 'Add new students',
        },
        {
          title: 'Student List',
          path: '/students',
          icon: Users,
          description: 'View all students',
        },
      ],
    },
    {
      id: 'attendance',
      title: 'Attendance',
      icon: CalendarDays,
      path: '/attendance',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      description: 'Track Attendance',
    },
    {
      id: 'batch',
      title: 'Batches & Classes',
      icon: Building2,
      path: '/batch',
      color: 'text-rose-600',
      bgColor: 'bg-rose-100',
      description: 'Manage batches & classes',
    },
    {
      id: 'finance',
      title: 'Finance',
      icon: IndianRupee,
      color: 'text-yellow-600', // Changed to 600 for better contrast
      bgColor: 'bg-yellow-100', // Changed to 100 for better consistency
      description: 'Financial Management',
      submenu: [
        {
          title: 'Financial Dashboard',
          path: '/financeDashboard',
          icon: IndianRupee, // Using IndianRupee
          description: 'Manage student fees',
        },
        {
          title: 'Fee Analytics',
          path: '/finance',
          icon: IndianRupee, // Using IndianRupee
          description: 'Manage student fees',
        },
        {
          title: 'Fee Report',
          path: '/fee',
          icon: PiggyBank,
          description: 'Fee collection report',
        },
        {
          title: 'Expenses',
          path: '/expanse',
          icon: ReceiptIndianRupee,
          description: 'Track expenses',
        },
        {
          title: 'HandOver-TakeOver',
          path: '/dailyCollection',
          icon: ClipboardList, // Changed icon for variety
          description: 'Manage handover-takeover records',
        },
        {
          title: 'Collections',
          path: '/collection',
          icon: PiggyBank, // Changed icon for variety
          description: 'Manage collections',
        },
        // Conditional submenu item - sirf admin aur manager ke liye
        ...(userRole === 'admin' || userRole === 'manager'
          ? [
              {
                title: 'Investments',
                path: '/investment',
                icon: PiggyBank,
                description: 'Capital Investment',
              },
            ]
          : []),
      ],
    },
    {
      id: 'certificates',
      title: 'Certificates',
      icon: ScrollText,
      path: '/generateCertificate',
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      description: 'Generate Certificates',
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: BookText,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      description: 'Analytics & Reports',
      submenu: [
        {
          title: 'Student Records',
          path: '/record',
          icon: Users,
          description: 'View student records',
        },
      ],
    },
    {
      id: 'employee',
      title: 'Employee',
      icon: UserCog,
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
      description: 'Employee Management',
      submenu: [
        {
          title: 'Employee registration',
          path: '/employee',
          icon: UserPlus,
          description: 'Employee registration',
        },
        {
          title: 'Employee List',
          path: '/employees',
          icon: Users,
          description: 'Employee List',
        },
      ],
    },

    {
      id: 'summerCourse',
      title: 'Summer Details',
      icon: ScrollText,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-100',
      description: 'Manage Summer Courses',
      submenu: [
        {
          title: 'Summer Dashboard',
          path: '/summerDashboard',
          icon: Users,
          description: 'Summer Dashboard',
        },
        {
          title: 'Summer Courses ',
          icon: ScrollText,
          path: '/summerCourse',
          description: 'Manage Summer Courses',
        },
      ],
    },
  ]

  const bottomMenuItems = [
    {
      id: 'laptop',
      title: 'Laptop Management',
      icon: Laptop,
      path: '/lapTopManage',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      description: 'Issue & Return Laptops',
    },
  ]

  const isActive = (path) => location.pathname === path

  const isAnySubmenuItemActive = (submenu) =>
    submenu?.some((item) => isActive(item.path))

  const isParentMenuActive = (item) => {
    if (item.submenu) {
      return isAnySubmenuItemActive(item.submenu)
    }
    return isActive(item.path)
  }

  const handleNavigation = (path) => {
    navigate(path)
    if (isMobile()) {
      // Collapse sidebar on mobile after navigation
      setIsCollapsed(true)
    }
  }

  // UseEffect to open parent menu if any of its subitems is active on initial load/route change
  useEffect(() => {
    let foundActiveSubmenu = false
    menuItems.forEach((item) => {
      if (item.submenu && isAnySubmenuItemActive(item.submenu)) {
        setOpenSubmenu(item.id)
        foundActiveSubmenu = true
      }
    })
    if (
      !foundActiveSubmenu &&
      menuItems.every((item) => item.path !== location.pathname)
    ) {
      // Only reset if the current path is NOT a top-level menu item path
      setOpenSubmenu(null)
    }
  }, [location.pathname])

  return (
    <>
      <aside
        className={`fixed left-0 top-0 h-full bg-white border-r border-gray-100 shadow-2xl z-50 transition-all duration-300 ease-in-out 
          ${
            isCollapsed
              ? 'w-20 lg:w-20 -translate-x-full lg:translate-x-0' // Collapsed state for mobile/desktop
              : 'w-72 translate-x-0' // Expanded state
          }`}
      >
        {/* Header/Logo Section */}
        <div
          className={`flex items-center p-4 border-b border-gray-100 h-[72px] transition-all duration-300
            ${isCollapsed ? 'justify-center' : 'justify-between'}`}
        >
          {!isCollapsed && (
            <div className='flex items-center space-x-3'>
              <div className='p-2 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl shadow-lg shadow-indigo-300/50'>
                <GraduationCap className='w-7 h-7 text-white' />
              </div>
              <div>
                <h1 className='font-extrabold text-2xl text-gray-900'>
                  Inflection
                </h1>
                <p className='text-xs font-medium text-indigo-500 tracking-wider'>
                  Management System
                </p>
              </div>
            </div>
          )}

          {/* Collapse/Expand Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-2 rounded-full bg-white border border-gray-200 text-gray-600 hover:bg-indigo-50 hover:text-indigo-600 transition-all duration-300 shadow-md ${
              isCollapsed ? 'hover:scale-105' : 'hover:scale-105'
            }`}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className='w-5 h-5' />
            ) : (
              <ChevronLeft className='w-5 h-5' />
            )}
          </button>
        </div>

        {/* Menu Items Container */}
        <div className='flex flex-col h-[calc(100vh-72px)]'>
          {/* Main Menu Items */}
          <div className='flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar'>
            {menuItems.map((item) => (
              <MenuItemComponent
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                openSubmenu={openSubmenu}
                setOpenSubmenu={setOpenSubmenu}
                handleNavigation={handleNavigation}
                isParentMenuActive={isParentMenuActive}
                isActive={isActive}
              />
            ))}
          </div>

          {/* Bottom Menu Items */}
          <div className='p-4 border-t border-gray-100 space-y-1'>
            {bottomMenuItems.map((item) => (
              <MenuItemComponent
                key={item.id}
                item={item}
                isCollapsed={isCollapsed}
                openSubmenu={openSubmenu}
                setOpenSubmenu={setOpenSubmenu}
                handleNavigation={handleNavigation}
                isParentMenuActive={isParentMenuActive}
                isActive={isActive}
              />
            ))}
          </div>

          {/* Footer Version Info */}
          {!isCollapsed && (
            <div className='p-4 bg-gray-50 border-t border-gray-100 text-center text-gray-500'>
              <p className='text-sm font-medium'>v1.2.0</p>
              <p className='text-xs'>© {new Date().getFullYear()} Inflection</p>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 
          ${isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        onClick={() => setIsCollapsed(true)}
      />

      {/* Adding a basic scrollbar style for better looks (Tailwind utility for custom-scrollbar is usually needed) */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #d1d5db; /* gray-300 */
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: transparent;
        }
      `}</style>
    </>
  )
}

export default Sidebar
